const DATABASE_NAME = 'smetter-knowledge-assistant';
const DATABASE_VERSION = 1;
const STORE_NAME = 'images';
const IMAGE_TTL_MS = 30 * 24 * 60 * 60 * 1000;

interface StoredImage {
    id: string;
    blob: Blob;
    createdAt: number;
}

let databasePromise: Promise<IDBDatabase> | null = null;
const pendingWrites = new Map<string, Promise<void>>();

const openDatabase = () => {
    if (databasePromise) return databasePromise;

    databasePromise = new Promise<IDBDatabase>((resolve, reject) => {
        const request = window.indexedDB.open(DATABASE_NAME, DATABASE_VERSION);

        request.onupgradeneeded = () => {
            if (!request.result.objectStoreNames.contains(STORE_NAME)) {
                request.result.createObjectStore(STORE_NAME, { keyPath: 'id' });
            }
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
        request.onblocked = () => reject(new Error('Knowledge assistant image storage is blocked'));
    });

    return databasePromise;
};

const writeImage = async (image: StoredImage) => {
    const database = await openDatabase();

    await new Promise<void>((resolve, reject) => {
        const transaction = database.transaction(STORE_NAME, 'readwrite');
        transaction.objectStore(STORE_NAME).put(image);
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(transaction.error);
        transaction.onabort = () => reject(transaction.error);
    });
};

export const createLocalImageId = () =>
    `image-${Date.now()}-${Math.random().toString(16).slice(2)}`;

export const saveLocalImage = (id: string, blob: Blob) => {
    const write = writeImage({ id, blob, createdAt: Date.now() });
    pendingWrites.set(id, write);
    void write.then(
        () => pendingWrites.delete(id),
        () => pendingWrites.delete(id)
    );

    return write;
};

export const getLocalImage = async (id: string): Promise<Blob | null> => {
    await pendingWrites.get(id);
    const database = await openDatabase();

    return new Promise((resolve, reject) => {
        const request = database.transaction(STORE_NAME).objectStore(STORE_NAME).get(id);
        request.onsuccess = () => {
            const image = request.result as StoredImage | undefined;
            resolve(image?.blob instanceof Blob ? image.blob : null);
        };
        request.onerror = () => reject(request.error);
    });
};

export const deleteExpiredLocalImages = async () => {
    const database = await openDatabase();
    const oldestAllowedCreatedAt = Date.now() - IMAGE_TTL_MS;

    await new Promise<void>((resolve, reject) => {
        const transaction = database.transaction(STORE_NAME, 'readwrite');
        const request = transaction.objectStore(STORE_NAME).openCursor();

        request.onsuccess = () => {
            const cursor = request.result;
            if (!cursor) return;

            const image = cursor.value as StoredImage;
            if (image.createdAt < oldestAllowedCreatedAt) cursor.delete();
            cursor.continue();
        };
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(transaction.error);
        transaction.onabort = () => reject(transaction.error);
    });
};
